import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { authApi, setToken, clearToken, getToken, ApiError } from "@/lib/api";

const AuthContext = createContext(undefined);
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "240001828458-t1uech4u8681itj0kt7cocivu5m3gefj.apps.googleusercontent.com";

function extractHashParamFrom(hash, name) {
  const m = (hash || "").match(new RegExp("[#&]" + name + "=([^&]+)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function extractHashParam(name) {
  const m = (window.location.hash || "").match(new RegExp("[#&]" + name + "=([^&]+)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function extractSessionId(url) {
  if (!url) return null;
  const m = url.match(/[?#&]session_id=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [loading, setLoading] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      // 1) Returning from managed Google OAuth: a session_id arrives in the URL hash/query.
      //    Process it FIRST (before any /auth/me check) to avoid a race, then clean the URL.
      const sid = extractSessionId(window.location.href);
      if (sid) {
        try {
          const res = await authApi.session(sid);
          setToken(res.access_token);
          setUser(res.user);
          const url = new URL(window.location.href);
          url.hash = "";
          url.searchParams.delete("session_id");
          window.history.replaceState(window.history.state, "", url.pathname + url.search);
          return;
        } catch {
          /* fall through to normal session check */
        }
      }
      // 1b) Returning from our own Google sign-in: #id_token=…&state=…
      const idTokenHash = window.location.hash || "";
      const idToken = extractHashParam("id_token");
      if (idToken) {
        let nonce = null, state = null, back = "/";
        try {
          nonce = sessionStorage.getItem("fable.g.nonce");
          state = sessionStorage.getItem("fable.g.state");
          back = sessionStorage.getItem("fable.g.back") || "/";
          ["fable.g.nonce", "fable.g.state", "fable.g.back"].forEach((k) => sessionStorage.removeItem(k));
        } catch (e) { /* private mode */ }
        window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
        if (state && extractHashParamFrom(idTokenHash, "state") === state) {
          try {
            const res = await authApi.google(idToken, nonce);
            setToken(res.access_token);
            setUser(res.user);
            if (back && back !== window.location.pathname) window.history.replaceState(window.history.state, "", back);
            return;
          } catch {
            /* fall through to normal session check */
          }
        }
      }
      // 2) Existing token → confirm with the server.
      const token = getToken();
      if (token) {
        const me = await authApi.me();
        if (me.renewed_token) setToken(me.renewed_token);
        setUser(me);
      }
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        clearToken();
        setUser(null);
      }
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      if (res.two_step_required) {
        // Caller shows the code step: { challenge_token, channel, hint, email_fallback }.
        return { two_step: true, challenge: res };
      }
      setToken(res.access_token);
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const completeTwoStep = useCallback(async (challenge_token, code, trust = true) => {
    setLoading(true);
    try {
      const res = await authApi.loginVerify(challenge_token, code, trust);
      setToken(res.access_token);
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await authApi.register(payload);
      setToken(res.access_token);
      setUser(res.user);
      return res.user;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(() => {
    // Our own Google OAuth client (ID-token flow, no secret on the client). Google returns to this origin's root with
    // #id_token=…&state=…; bootstrap() verifies it with the backend. Falls back to the hosted page only if unset.
    if (GOOGLE_CLIENT_ID) {
      const rand = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
      const nonce = rand();
      const state = rand();
      try {
        sessionStorage.setItem("fable.g.nonce", nonce);
        sessionStorage.setItem("fable.g.state", state);
        sessionStorage.setItem("fable.g.back", window.location.pathname + window.location.search);
      } catch (e) { /* private mode */ }
      const q = new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID, response_type: "id_token", scope: "openid email profile",
        redirect_uri: window.location.origin + "/", nonce, state, prompt: "select_account",
      });
      window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?" + q.toString();
      return;
    }
    const redirect = window.location.origin + "/";
    window.location.href = "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
  }, []);

  const loginWithApple = useCallback(async (identity_token, full_name) => {
    const res = await authApi.apple(identity_token, full_name);
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await Promise.race([authApi.logout().catch(() => null), new Promise((r) => setTimeout(r, 1500))]);
    } catch {
      /* ignore */
    }
    clearToken();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      if (me.renewed_token) setToken(me.renewed_token);
      setUser(me);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({ user, bootstrapped, loading, login, completeTwoStep, register, loginWithGoogle, loginWithApple, logout, refresh, setUser }),
    [user, bootstrapped, loading, login, completeTwoStep, register, loginWithGoogle, loginWithApple, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
